"use client";

import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const DocumentTypeContext = createContext();

export const DocumentTypeProvider = ({ children }) => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Fetch all document types
  const fetchDocumentTypes = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/document-types`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch document types");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setDocumentTypes(data.data || []);
      } else {
        setError(data.message || "Failed to fetch document types");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching document types");
    } finally {
      setLoading(false);
    }
  };

  // Create document type
  const createDocumentType = async (payload) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/document-types`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to create document type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 201) {
        await fetchDocumentTypes();

        toast.success(data.message || "Document type created successfully");

        return {
          success: true,
          message: data.message || "Document type created successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create document type",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error creating document type",
      };
    } finally {
      setLoading(false);
    }
  };

  // Update document type
  const updateDocumentType = async (id, payload) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/document-types/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update document type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchDocumentTypes();

        toast.success(data.message || "Document type updated successfully");

        return {
          success: true,
          message: data.message || "Document type updated successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update document type",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating document type",
      };
    } finally {
      setLoading(false);
    }
  };

  // Update status
  const updateDocumentTypeStatus = async (id, status, update_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(
        `${BASE_URL}/document-types/update-status/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status,
            update_audit_id,
          }),
        }
      );

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update document type status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchDocumentTypes();

        toast.success(
          data.message || "Document type status updated successfully"
        );

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update document type status",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating document type status",
      };
    }
  };

  // Soft delete
  const deleteDocumentType = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/document-types/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id,
        }),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to delete document type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchDocumentTypes();

        toast.success(data.message || "Document type deleted successfully");

        return {
          success: true,
          message: data.message || "Document type deleted successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete document type",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error deleting document type",
      };
    }
  };

  useEffect(() => {
    const token = getToken();
    if (token) fetchDocumentTypes();
  }, []);

  return (
    <DocumentTypeContext.Provider
      value={{
        documentTypes,
        loading,
        error,
        fetchDocumentTypes,
        createDocumentType,
        updateDocumentType,
        updateDocumentTypeStatus,
        deleteDocumentType,
      }}
    >
      {children}
    </DocumentTypeContext.Provider>
  );
};

export const useDocumentTypes = () => useContext(DocumentTypeContext);